import { EMPTY_VALUE } from './dates';

/**
 * CNPJ, the Brazilian company registry number: fourteen characters, rendered
 * `00.000.000/0000-00`.
 *
 * Unlike [`formatCpf`](./cpf.ts) there is nothing to mask here. A CNPJ is
 * public registry data -- the Receita Federal publishes it and it is printed on
 * every invoice -- so it is shown in full, and showing it in full is the point:
 * it is what somebody on the phone to an insurer reads out.
 *
 * **Letters are not a typo.** From July 2026 the Receita Federal issues CNPJs
 * whose twelve establishment positions may each be `0`-`9` or `A`-`Z`, with the
 * two check digits still numeric (`12.ABC.345/01DE-35`). So the canonical form
 * this renders is fourteen *characters* rather than fourteen digits, and nothing
 * here may strip a letter.
 *
 * There is deliberately no check-digit function beside this one. The API's
 * validator is the authority, it answers in one round trip, and a second copy of
 * a rule that changed alphabets once already is a second copy to keep right.
 */

/** Everything a CNPJ is written with. Lower case is accepted and upper-cased. */
const OUTSIDE_THE_ALPHABET = /[^0-9A-Z]/g;

export const CNPJ_LENGTH = 14;

/** The canonical form: fourteen characters, no punctuation, letters upper-cased. */
export function stripCnpj(value: string): string {
  return value.toUpperCase().replace(OUTSIDE_THE_ALPHABET, '');
}

/**
 * `12345678000195` becomes `12.345.678/0001-95`.
 *
 * A blank or absent value renders as the same em dash every other empty cell
 * uses -- which is what a company registered before the field existed shows
 * until somebody fills it in. Anything of an unexpected length is returned
 * unchanged rather than mangled into a shape that looks authoritative.
 */
export function formatCnpj(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }
  const characters = stripCnpj(value);
  if (characters.length !== CNPJ_LENGTH) {
    return value;
  }
  return (
    `${characters.slice(0, 2)}.${characters.slice(2, 5)}.${characters.slice(5, 8)}` +
    `/${characters.slice(8, 12)}-${characters.slice(12)}`
  );
}
